import { useLayoutEffect, useState, type RefObject } from "react";
import Konva from "konva";

import type { TimelineStatusRun, TrackingTimeline } from "../../tracking/player/timeline-status";
import type { TRenderableSegment } from "../../utils/tree";
import { mergeRanges, type TimeRange } from "../../utils/ranges";
import { SEGMENT_GROWTH_MS, segmentGrowthEnd, type SegmentGrowth } from "./useSegmentGrowth";
import { RETRACTION_MS, segmentRetractionBounds, type SegmentRetraction } from "./useSegmentRetractions";

type Range = TimeRange;
/** An immutable trajectory in video seconds, with a mask that can shrink if coverage is removed. */
export type CoverageArrival = { from: number; to: number; ranges: readonly Range[]; startedAt: number };
const REVEAL_MS = 220;
const STRIPE_SPACING = 16;
const STRIPE_WIDTH = 6;
const STRIPE_SPEED_PX_PER_MS = 0.032;
const STRIPE_OPACITY = 0.24;

const isFinished = (run: TimelineStatusRun) => run.state === "ready" || run.state === "bridged";

/** Sorted, disjoint ranges present in current coverage but absent from previous coverage. */
export function addedCoverage(current: readonly Range[], previous: readonly Range[]): Range[] {
  const added: Range[] = [];
  let previousIndex = 0;
  for (const range of current) {
    let start = range.start;
    while (previous[previousIndex] && previous[previousIndex]!.end <= start) previousIndex++;
    for (let i = previousIndex; previous[i] && previous[i]!.start < range.end; i++) {
      const old = previous[i]!;
      if (old.start > start) added.push({ start, end: Math.min(old.start, range.end) });
      start = Math.max(start, old.end);
      if (start >= range.end) break;
    }
    if (start < range.end) added.push({ start, end: range.end });
  }
  return added;
}

function intersectCoverage(ranges: readonly Range[], coverage: readonly Range[]): Range[] {
  const result: Range[] = [];
  let first = 0;
  for (const range of ranges) {
    while (coverage[first] && coverage[first]!.end <= range.start) first++;
    for (let i = first; coverage[i] && coverage[i]!.start < range.end; i++) {
      const start = Math.max(range.start, coverage[i]!.start);
      const end = Math.min(range.end, coverage[i]!.end);
      if (start < end) result.push({ start, end });
    }
  }
  return result;
}

function unrevealedCoverage(arrival: CoverageArrival, now: number): Range[] {
  const progress = Math.max(0, Math.min(1, (now - arrival.startedAt) / REVEAL_MS));
  const front = arrival.from + (arrival.to - arrival.from) * (1 - (1 - progress) ** 3);
  return intersectCoverage(arrival.ranges, [{ start: front, end: arrival.to }]);
}

/** Extend touching fronts from their current position without re-covering visible islands. */
function extendReveals(ongoing: readonly CoverageArrival[], added: readonly Range[], now: number): CoverageArrival[] {
  if (!added.length) return [...ongoing];
  type Piece = Range & { arrival?: CoverageArrival };
  const pieces: Piece[] = [
    ...ongoing.flatMap((arrival) => unrevealedCoverage(arrival, now).map((range) => ({ ...range, arrival }))),
    ...mergeRanges(added),
  ].sort((a, b) => a.start - b.start);
  const extended: CoverageArrival[] = [];
  const retained = new Map<CoverageArrival, Range[]>();
  for (let first = 0; first < pieces.length; ) {
    let last = first + 1;
    let end = pieces[first]!.end;
    let hasAddition = !pieces[first]!.arrival;
    while (last < pieces.length && pieces[last]!.start <= end) {
      end = Math.max(end, pieces[last]!.end);
      hasAddition ||= !pieces[last]!.arrival;
      last++;
    }
    if (hasAddition) {
      const start = pieces[first]!.start;
      extended.push({ from: start, to: end, ranges: [{ start, end }], startedAt: now });
    } else {
      for (let i = first; i < last; i++) {
        const { start, end: pieceEnd, arrival } = pieces[i]!;
        const ranges = retained.get(arrival!) ?? [];
        ranges.push({ start, end: pieceEnd });
        retained.set(arrival!, ranges);
      }
    }
    first = last;
  }
  const retainedArrivals = [...retained].map(([arrival, ranges]) => ({ ...arrival, ranges }));
  return [...retainedArrivals, ...extended];
}

/** Keep reveal fronts independent of other tracks, pan, and zoom. */
export function useCoverageAdditions(timeline: TrackingTimeline | null, segments: readonly TRenderableSegment[]) {
  const [snapshot, setSnapshot] = useState(() => ({
    timeline,
    segments,
    additions: new Map<string, readonly CoverageArrival[]>(),
  }));
  if (snapshot.timeline === timeline && snapshot.segments === segments) return snapshot.additions;

  const additions = new Map<string, readonly CoverageArrival[]>();
  if (snapshot.timeline?.revision !== timeline?.revision) {
    setSnapshot({ timeline, segments, additions });
    return additions;
  }
  const now = performance.now();
  for (const { id } of segments) {
    const current = timeline?.segments.get(id)?.runs.filter(isFinished) ?? [];
    const previous = snapshot.timeline?.segments.get(id)?.runs ?? [];
    const previousReady = previous.filter(isFinished);
    const existing = snapshot.additions.get(id);
    const unchanged =
      current.length === previousReady.length &&
      current.every((range, i) => range.start === previousReady[i]!.start && range.end === previousReady[i]!.end);
    if (unchanged && existing?.every((arrival) => now - arrival.startedAt < REVEAL_MS)) {
      additions.set(id, existing);
      continue;
    }
    // Retain each clock, clipped to current coverage. A removed/reappearing
    // span starts fresh; cached mounts and newly streamed GT do not replay.
    const ongoing = (existing ?? []).flatMap((arrival) => {
      if (now - arrival.startedAt >= REVEAL_MS) return [];
      const ranges = mergeRanges(intersectCoverage(arrival.ranges, current));
      return ranges.length ? [{ ...arrival, ranges }] : [];
    });
    const pending = previous.filter((run) => run.state === "pending");
    const ranges = intersectCoverage(addedCoverage(current, previousReady), pending);
    const next = extendReveals(ongoing, ranges, now);
    if (next.length) additions.set(id, next);
  }
  setSnapshot({ timeline, segments, additions });
  return additions;
}

/** Grow the solid fill by clearing stripes from left to right, without fading either fill. */
export function useCoverageReveal(
  ref: RefObject<Konva.Rect | null>,
  segment: TRenderableSegment,
  pixelsPerSecond: number,
  runs: readonly TimelineStatusRun[] | undefined,
  arrivals: readonly CoverageArrival[] | undefined,
  growth?: SegmentGrowth,
  retraction?: SegmentRetraction,
) {
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node || (!runs && !growth && !retraction)) return;
    const whole = { start: segment.start, end: segment.end! };
    const finalBounds = { ...whole, leftRadius: 1, rightRadius: 1 };
    const ready = intersectCoverage(runs?.filter(isFinished) ?? [], [whole]);
    const pending = intersectCoverage(runs?.filter((run) => run.state === "pending") ?? [], [whole]);
    const motion = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    let now = motion?.matches ? 0 : performance.now();
    let expanding = !motion?.matches && growth && now - growth.startedAt < SEGMENT_GROWTH_MS ? growth : undefined;
    let retracting =
      !motion?.matches && retraction && now - retraction.startedAt < RETRACTION_MS ? retraction : undefined;
    let reveals = motion?.matches ? [] : (arrivals ?? []).filter((arrival) => now - arrival.startedAt < REVEAL_MS);
    const finishAt = Math.max(...reveals.map((arrival) => arrival.startedAt)) + REVEAL_MS;
    const draw = node.getSceneFunc();
    const hit = node.getHitFunc();
    node.hitFunc(hit ?? draw);
    node.sceneFunc((context, shape) => {
      const bounds = retracting ? segmentRetractionBounds(retracting, now) : finalBounds;
      const left = (bounds.start - whole.start) * pixelsPerSecond;
      const right =
        (Math.min(bounds.end, expanding ? segmentGrowthEnd(expanding, now) : bounds.end) - whole.start) *
        pixelsPerSecond;
      const width = right - left;
      if (width <= 0) return;
      const radius = Math.min(4, width / 2, node.height() / 2);
      const radii = retracting
        ? [
            radius * bounds.leftRadius,
            radius * bounds.rightRadius,
            radius * bounds.rightRadius,
            radius * bounds.leftRadius,
          ]
        : radius;
      // Draw the growing silhouette with the shape's existing fill and shadow.
      // Retiring edges may extend beyond either side of the final hit rectangle.
      // The node and hit canvas retain exact geometry; stripes are never scaled.
      if (retracting || left !== 0 || width !== node.width()) {
        context.beginPath();
        context.roundRect(left, 0, width, node.height(), radii);
        context.closePath();
        context.fillStrokeShape(shape);
      } else {
        draw.call(node, context, shape);
      }
      const paintStripes = (ranges: readonly Range[]) => {
        if (!ranges.length) return;
        context.save();
        context.beginPath();
        for (const range of ranges) {
          context.rect(
            (range.start - whole.start) * pixelsPerSecond,
            0,
            (range.end - range.start) * pixelsPerSecond,
            node.height(),
          );
        }
        context.clip();
        context.beginPath();
        context.roundRect(left, 0, width, node.height(), radii);
        context.clip();
        context.globalAlpha *= STRIPE_OPACITY;
        context.globalCompositeOperation = "multiply";
        // Shade with the same orange gradient so bands do not flatten the bar's
        // vertical lighting or introduce another palette at the transition.
        const stripeGradient = context.createLinearGradient(0, node.height(), 0, 0);
        const stops = node.fillLinearGradientColorStops();
        for (let i = 0; i < stops.length; i += 2)
          stripeGradient.addColorStop(stops[i] as number, stops[i + 1] as string);
        context.strokeStyle = stripeGradient;
        context.lineWidth = STRIPE_WIDTH;
        context.shadowBlur = 0;
        context.shadowColor = "transparent";
        context.beginPath();
        // Do not iterate across an offscreen, highly zoomed-in track.
        const viewportLeft = Math.max(left, -node.getAbsolutePosition().x);
        const viewportRight = Math.min(right, (node.getStage()?.width() ?? right) - node.getAbsolutePosition().x);
        // Timeline-space phase survives publications and fragment splits.
        const phase =
          (((now * STRIPE_SPEED_PX_PER_MS - whole.start * pixelsPerSecond) % STRIPE_SPACING) + STRIPE_SPACING) %
          STRIPE_SPACING;
        for (
          let x = Math.floor(viewportLeft / STRIPE_SPACING) * STRIPE_SPACING - node.height() - STRIPE_SPACING + phase;
          x < viewportRight + STRIPE_SPACING;
          x += STRIPE_SPACING
        ) {
          // Extend the diagonal past both clip edges. Ending its centerline at
          // the edge leaves part of the stroke's flat end cap inside the bar.
          context.moveTo(x - STRIPE_WIDTH, node.height() + STRIPE_WIDTH);
          context.lineTo(x + node.height() + STRIPE_WIDTH, -STRIPE_WIDTH);
        }
        context.stroke();
        context.restore();
      };
      // One stripe pass keeps a continuous pattern across status boundaries.
      // Evaluate the original trajectory before clipping to a split bar or zoomed viewport.
      paintStripes(
        mergeRanges([
          ...pending,
          ...(retracting?.pending ?? []),
          ...reveals.flatMap((arrival) => intersectCoverage(unrevealedCoverage(arrival, now), ready)),
        ]),
      );
    });
    let animation: Konva.Animation | undefined;
    const updateMotion = () => {
      now = motion?.matches ? 0 : performance.now();
      if (motion?.matches) {
        animation?.stop();
        reveals = [];
        expanding = undefined;
        retracting = undefined;
      } else if (pending.length || reveals.length || expanding || retracting) {
        animation ??= new Konva.Animation(() => {
          now = performance.now();
          if (reveals.length && now >= finishAt) {
            reveals = [];
          }
          if (expanding && now - expanding.startedAt >= SEGMENT_GROWTH_MS) expanding = undefined;
          if (retracting && now - retracting.startedAt >= RETRACTION_MS) retracting = undefined;
          if (!pending.length && !reveals.length && !expanding && !retracting) animation?.stop();
        }, node.getLayer());
        animation.start();
      }
      node.getLayer()?.batchDraw();
    };
    motion?.addEventListener("change", updateMotion);
    updateMotion();
    return () => {
      motion?.removeEventListener("change", updateMotion);
      animation?.stop();
      node.sceneFunc(draw);
      node.setAttr("hitFunc", hit);
      node.getLayer()?.batchDraw();
    };
  }, [ref, segment.start, segment.end, pixelsPerSecond, runs, arrivals, growth, retraction]);
}
